import React, { useCallback } from 'react';

import Account from '@atlassian/account/account/nav3';

import { useThemingState } from '@confluence/theming/entry-points/useThemingState.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { fg } from '@confluence/feature-gating';
import { fetchGlobalColorMode } from '@confluence/theming/entry-points/useGlobalColorMode.ts';
import { useColorModeState } from '@atlassian/shared-user-preferences-api/global-theme-management';

import { getIdentityEnv } from './getIdentityEnv.ts';
import type { User } from './CompanyHubTopNavigation.tsx';

type CompanyHubProfileMenuProps = {
	user: User;
};

export const CompanyHubProfileMenu = ({ user }: CompanyHubProfileMenuProps): React.JSX.Element => {
	const [themeState, themingActions] = useThemingState();
	const getThemePreference = useCallback(() => fetchGlobalColorMode(user.id), [user.id]);
	const [globalColorMode, loadGlobalColorModeOnce] = useColorModeState(getThemePreference, user.id);
	const identityEnvironment = getIdentityEnv(user.environment);
	const primaryEmail = user?.emails?.find((email) => email?.isPrimary)?.value || '';
	const displayName = user?.displayName || '';
	const photo = user?.photos?.find((photo) => photo?.isPrimary)?.value || '';
	const renderServerPlaceholder = useRenderServerPlaceholder();
	// Only set continueUrl if we are not in SSR mode and we are in the browser to avoid breaking SSR
	const continueUrl =
		// eslint-disable-next-line no-restricted-syntax
		!renderServerPlaceholder && typeof window !== 'undefined' ? window.location.href : '';

	return (
		<Account
			globalColorMode={fg('shared-user-preferences') ? globalColorMode : undefined}
			onThemeSwitcherOpenChange={loadGlobalColorModeOnce}
			testId="company-hub-profile-button"
			application="confluence"
			email={primaryEmail}
			identityEnvironment={identityEnvironment}
			continueUrl={continueUrl}
			name={displayName}
			picture={photo}
			currentTheme={themeState?.theme?.colorMode}
			onThemeChange={themingActions.setColorModePreference}
		/>
	);
};
